import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import View from '../common/View';
import Text from '../common/Text';
import Button from '../common/Button';
import BaseTemplate from '../components/BaseTemplate';
import './Settings.css';

const Settings: React.FC = () => {
  const { t } = useLanguage();
  const { logout, deleteAccount, isAuthenticated, isGuest } = useAuth();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSignOut = async () => {
    await logout();
    navigate('/signin');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      navigate('/signin');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <BaseTemplate
      title={t('SETTINGS') || 'Settings'}
      subtitle={t('SETTINGS_SUBTITLE') || 'Manage your account'}
    >
      <View className="settings">
        <View className="settings-section">
          <Text variant="h3">{t('ACCOUNT') || 'Account'}</Text>
          <View className="settings-row">
            <Text variant="body">{t('STATUS') || 'Status'}</Text>
            <Text variant="body" className="settings-value">
              {isAuthenticated
                ? isGuest
                  ? t('STATUS_GUEST') || 'Guest'
                  : t('STATUS_AUTHENTICATED') || 'Signed in'
                : t('STATUS_UNAUTHENTICATED') || 'Not signed in'}
            </Text>
          </View>
        </View>

        <View className="settings-section">
          <Text variant="h3">{t('LANGUAGE') || 'Language'}</Text>
          <Text variant="body" className="settings-muted">
            {t('LANGUAGE_PLACEHOLDER') || 'Language picker coming next pass.'}
          </Text>
        </View>

        <View className="settings-section">
          <Text variant="h3">{t('SUPPORT') || 'Support'}</Text>
          <View className="settings-links">
            <a
              href="https://wunderguide.com/support"
              target="_blank"
              rel="noreferrer"
            >
              {t('CONTACT_SUPPORT') || 'Contact support'}
            </a>
            <a
              href="https://wunderguide.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              {t('PRIVACY_POLICY') || 'Privacy policy'}
            </a>
            <a
              href="https://wunderguide.com/terms"
              target="_blank"
              rel="noreferrer"
            >
              {t('TERMS_OF_SERVICE') || 'Terms of service'}
            </a>
          </View>
        </View>

        <View className="settings-section settings-danger">
          <Text variant="h3">{t('DANGER_ZONE') || 'Danger zone'}</Text>
          <View className="settings-actions">
            <Button variant="secondary" size="medium" onClick={handleSignOut}>
              {t('SIGN_OUT') || 'Sign out'}
            </Button>
            {!isGuest && (
              <Button
                variant="danger"
                size="medium"
                onClick={() => setShowDeleteConfirm(true)}
              >
                {t('DELETE_ACCOUNT') || 'Delete account'}
              </Button>
            )}
          </View>
        </View>

        {showDeleteConfirm && (
          <View className="settings-confirm">
            <Text variant="body">
              {t('DELETE_ACCOUNT_CONFIRM') ||
                'This permanently deletes your account and all data. This cannot be undone.'}
            </Text>
            <View className="settings-actions">
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button
                variant="danger"
                size="medium"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting
                  ? t('DELETING') || 'Deleting…'
                  : t('CONFIRM_DELETE') || 'Yes, delete my account'}
              </Button>
            </View>
          </View>
        )}

        <View className="settings-back">
          <Link to="/">{t('BACK_TO_DASHBOARD') || '← Back to dashboard'}</Link>
        </View>
      </View>
    </BaseTemplate>
  );
};

export default Settings;
